import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { Button } from '../ui/button';
import { X, Building2, Users, GitBranch, CreditCard, Wallet, Ticket, Eye } from 'lucide-react';

interface Props {
  tenantId: string | null;
  onClose: () => void;
}

export function ViewAccountModal({ tenantId, onClose }: Props) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setData(null);
      return;
    }
    const fetchAccount = async () => {
      const supabase = getSupabaseClient();
      if (!supabase) return;
      setLoading(true);
      setError(null);
      const { data: result, error: rpcError } = await supabase.rpc('admin_view_tenant_account', {
        p_tenant_id: tenantId,
      });
      if (rpcError) {
        setError(rpcError.message);
        toast.error(rpcError.message || 'Failed to load account');
      } else {
        setData(result);
      }
      setLoading(false);
    };
    fetchAccount();
  }, [tenantId]);

  if (!tenantId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-xl bg-white dark:bg-slate-900 border dark:border-slate-800 shadow-xl">
        <div className="sticky top-0 bg-white dark:bg-slate-900 border-b dark:border-slate-800 px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-bold dark:text-white flex items-center gap-2">
            <Eye size={18} /> Account View (Read-Only)
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {loading && <div className="text-center text-slate-500 dark:text-slate-400 py-12">Loading account snapshot...</div>}
          {error && <div className="text-center text-red-500 py-12">{error}</div>}

          {data && (
            <>
              <section>
                <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                  <Building2 size={14} /> Tenant Profile
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <div><span className="text-slate-400 block text-xs">Business</span><span className="dark:text-white font-medium">{data.tenant?.business_name}</span></div>
                  <div><span className="text-slate-400 block text-xs">Type</span><span className="dark:text-white font-medium capitalize">{data.tenant?.business_type}</span></div>
                  <div><span className="text-slate-400 block text-xs">Status</span><span className="dark:text-white font-medium capitalize">{data.tenant?.status}</span></div>
                  <div><span className="text-slate-400 block text-xs">Approved?</span><span className="dark:text-white font-medium">{data.tenant?.is_approved ? 'Yes' : 'No'}</span></div>
                  <div><span className="text-slate-400 block text-xs">Commission Override</span><span className="dark:text-white font-medium">{data.tenant?.commission_override_percent != null ? `${data.tenant.commission_override_percent}%` : '—'}</span></div>
                  <div><span className="text-slate-400 block text-xs">Created</span><span className="dark:text-white font-medium">{data.tenant?.created_at ? new Date(data.tenant.created_at).toLocaleDateString() : '—'}</span></div>
                </div>
              </section>

              <section>
                <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                  <Users size={14} /> Staff Roster ({data.staff?.length || 0})
                </h4>
                <div className="border dark:border-slate-800 rounded-lg overflow-hidden">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 dark:bg-slate-800/50 text-xs text-slate-500 dark:text-slate-400">
                      <tr><th className="px-4 py-2">Name</th><th className="px-4 py-2">Role</th><th className="px-4 py-2">Contact</th><th className="px-4 py-2">Active</th></tr>
                    </thead>
                    <tbody className="divide-y dark:divide-slate-800">
                      {(data.staff || []).map((s: any, i: number) => (
                        <tr key={i}>
                          <td className="px-4 py-2 dark:text-white">{s.full_name}</td>
                          <td className="px-4 py-2 dark:text-slate-300 capitalize">{s.role_name}</td>
                          <td className="px-4 py-2 dark:text-slate-400 text-xs">{s.email || s.phone}</td>
                          <td className="px-4 py-2">{s.is_active ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                      {(!data.staff || data.staff.length === 0) && (
                        <tr><td colSpan={4} className="px-4 py-3 text-center text-slate-400 text-xs">No staff found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                  <GitBranch size={14} /> Branches ({data.branches?.length || 0})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {(data.branches || []).map((b: any, i: number) => (
                    <span key={i} className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg text-xs dark:text-slate-300">
                      {b.branch_name} — {b.city} {b.pincode} <span className="text-slate-400">({b.status})</span>
                    </span>
                  ))}
                  {(!data.branches || data.branches.length === 0) && <span className="text-xs text-slate-400">No branches found</span>}
                </div>
              </section>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <section>
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                    <CreditCard size={14} /> Subscriptions
                  </h4>
                  <div className="space-y-2">
                    {(data.subscriptions || []).map((s: any, i: number) => (
                      <div key={i} className="text-xs p-2 bg-slate-50 dark:bg-slate-800 rounded flex justify-between">
                        <span className="dark:text-slate-300">{s.plan_name} ({s.module_name})</span>
                        <span className="dark:text-white font-medium capitalize">{s.status}</span>
                      </div>
                    ))}
                    {(!data.subscriptions || data.subscriptions.length === 0) && <span className="text-xs text-slate-400">No subscriptions</span>}
                  </div>
                </section>

                <section>
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                    <Wallet size={14} /> Wallets
                  </h4>
                  <div className="space-y-2">
                    {(data.wallets || []).map((w: any, i: number) => (
                      <div key={i} className="text-xs p-2 bg-slate-50 dark:bg-slate-800 rounded flex justify-between">
                        <span className="dark:text-slate-300">{w.account_label}</span>
                        <span className="dark:text-white font-medium">₹ {Number(w.current_balance || 0).toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                    {(!data.wallets || data.wallets.length === 0) && <span className="text-xs text-slate-400">No wallets</span>}
                  </div>
                </section>
              </div>

              <section className="flex items-center gap-2 text-sm">
                <Ticket size={16} className="text-slate-400" />
                <span className="dark:text-slate-300">Open support tickets:</span>
                <span className="font-bold dark:text-white">{data.open_support_tickets ?? 0}</span>
              </section>

              <p className="text-xs text-slate-400 text-right">Viewed at {data.viewed_at ? new Date(data.viewed_at).toLocaleString() : ''}</p>
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-slate-900 border-t dark:border-slate-800 px-6 py-4 flex justify-end">
          <Button variant="outline" onClick={onClose} className="dark:border-slate-700 dark:text-white">Close</Button>
        </div>
      </div>
    </div>
  );
}

export default ViewAccountModal;
